<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import {
  cortexCurationProposalsAPI,
  cortexKnowledgeBasesAPI,
  cortexLearningCandidatesAPI,
  cortexProcessingRunsAPI,
} from 'dashboard/api/cortex';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextCheckbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import BaseTable from 'dashboard/components-next/table/BaseTable.vue';
import BaseTableCell from 'dashboard/components-next/table/BaseTableCell.vue';
import BaseTableRow from 'dashboard/components-next/table/BaseTableRow.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import SectionLayout from '../../settings/account/components/SectionLayout.vue';
import CortexProcessingBudgetDialog from './CortexProcessingBudgetDialog.vue';
import CortexCurationDraftEditor from './CortexCurationDraftEditor.vue';

const props = defineProps({
  assessmentId: { type: Number, default: null },
  conversationDisplayId: { type: Number, default: null },
});
const activeAssessmentId = ref(props.assessmentId);
const { t } = useI18n();
const { formatMessage } = useMessageFormatter();
const variationKinds = computed(() => [
  {
    key: 'equivalent',
    label: t('CORTEX.CURATION_V6.VARIATION_KINDS.equivalent'),
  },
  {
    key: 'conditional',
    label: t('CORTEX.CURATION_V6.VARIATION_KINDS.conditional'),
  },
  { key: 'related', label: t('CORTEX.CURATION_V6.VARIATION_KINDS.related') },
  {
    key: 'contrastive',
    label: t('CORTEX.CURATION_V6.VARIATION_KINDS.contrastive'),
  },
]);
const candidateLoading = ref(false);
const proposalLoading = ref(false);
const actingId = ref(null);
const candidates = ref([]);
const proposals = ref([]);
const knowledgeBases = ref([]);
const candidateTotal = ref(0);
const proposalTotal = ref(0);
const candidatePage = ref(1);
const proposalPage = ref(1);
const candidatePerPage = ref(10);
const proposalPerPage = ref(10);
const selectedCandidateIds = ref([]);
const selectedProposalIds = ref([]);
const candidateDialog = ref(null);
const reviewDialog = ref(null);
const bulkConfirmDialog = ref(null);
const selectedProposal = ref(null);
const editingDraft = ref(false);
const draftError = ref('');
const draftStale = ref(false);
let draftOperation;
const budgetDialog = ref(null);
const resumeOperation = ref(null);
const resumeNeedsRefresh = ref(false);
const isReviewable = proposal =>
  ['pending', 'in_review'].includes(proposal?.status);
const reviewableProposals = computed(() =>
  proposals.value.filter(isReviewable)
);
const questionStyleLabels = computed(() => ({
  direct: t('CORTEX.CURATION_V6.QUESTION_STYLES.direct'),
  informal: t('CORTEX.CURATION_V6.QUESTION_STYLES.informal'),
  technical: t('CORTEX.CURATION_V6.QUESTION_STYLES.technical'),
  commercial: t('CORTEX.CURATION_V6.QUESTION_STYLES.commercial'),
  operational: t('CORTEX.CURATION_V6.QUESTION_STYLES.operational'),
  indirect: t('CORTEX.CURATION_V6.QUESTION_STYLES.indirect'),
  contextual: t('CORTEX.CURATION_V6.QUESTION_STYLES.contextual'),
  limitation: t('CORTEX.CURATION_V6.QUESTION_STYLES.limitation'),
}));
const coverageReport = computed(
  () => selectedProposal.value?.payload?.variation_generation?.coverage_report
);
const styleCoverage = computed(() =>
  Object.entries(questionStyleLabels.value).map(([key, label]) => ({
    key,
    label,
    count: coverageReport.value?.style_counts?.[key] || 0,
  }))
);
const verifiedQuestionStyle = index => {
  const verdict = coverageReport.value?.verdicts?.find(
    item => item.index === index
  );
  return questionStyleLabels.value[verdict?.style] || '';
};
const questionCoverageVerdict = (kind, index) => {
  if (index < 0 || !['equivalent', 'conditional', 'observed'].includes(kind))
    return null;
  const report = coverageReport.value;
  if (!report) return '';
  const offsets = {
    equivalent: 0,
    conditional: report.equivalent_count,
    observed: report.equivalent_count + report.conditional_count,
  };
  return report.verdicts?.find(item => item.index === offsets[kind] + index);
};
const questionCoverageLabel = (kind, index) => {
  const verdict = questionCoverageVerdict(kind, index);
  if (!verdict) return '';
  return verdict.equivalent && verdict.answer_complete
    ? t('CORTEX.CURATION_V6.ANSWER_COVERED')
    : t('CORTEX.CURATION_V6.ANSWER_NOT_COVERED');
};
const questionCoverageReason = (kind, index) => {
  const verdict = questionCoverageVerdict(kind, index);
  return verdict && (!verdict.equivalent || !verdict.answer_complete)
    ? verdict.reason || ''
    : '';
};
const observedQuestionIndex = question => {
  const normalize = value => value?.replace(/\s+/g, ' ').trim();
  const questions = [
    selectedProposal.value?.payload?.question,
    ...(selectedProposal.value?.candidate_questions || []).map(
      item => item.question
    ),
  ].map(normalize);
  return [...new Set(questions)].indexOf(normalize(question));
};
const observedQuestionCoverageLabel = question =>
  questionCoverageLabel('observed', observedQuestionIndex(question));
const observedQuestionCoverageReason = question =>
  questionCoverageReason('observed', observedQuestionIndex(question));
const temporalLabels = computed(() => ({
  stable: t('CORTEX.CURATION_V6.TEMPORAL_CLASSES.stable'),
  temporal: t('CORTEX.CURATION_V6.TEMPORAL_CLASSES.temporal'),
  version_dependent: t('CORTEX.CURATION_V6.TEMPORAL_CLASSES.version_dependent'),
  pending: t('CORTEX.CURATION_V6.TEMPORAL_CLASSES.pending'),
}));
const knowledgeFields = computed(() => {
  const metadata = selectedProposal.value?.payload?.knowledge_metadata || {};
  const labels = {
    category: t('CORTEX.CURATION_V6.KNOWLEDGE_METADATA.category'),
    subcategory: t('CORTEX.CURATION_V6.KNOWLEDGE_METADATA.subcategory'),
    intent: t('CORTEX.CURATION_V6.KNOWLEDGE_METADATA.intent'),
    keywords: t('CORTEX.CURATION_V6.KNOWLEDGE_METADATA.keywords'),
    synonyms: t('CORTEX.CURATION_V6.KNOWLEDGE_METADATA.synonyms'),
    exceptions: t('CORTEX.CURATION_V6.KNOWLEDGE_METADATA.exceptions'),
    temporal_classification: t(
      'CORTEX.CURATION_V6.KNOWLEDGE_METADATA.temporal_classification'
    ),
    validated_at: t('CORTEX.CURATION_V6.KNOWLEDGE_METADATA.validated_at'),
  };
  return Object.entries(labels)
    .filter(([key]) => metadata[key]?.length)
    .map(([key, label]) => {
      let value = Array.isArray(metadata[key])
        ? metadata[key].join(', ')
        : metadata[key];
      if (key === 'temporal_classification')
        value = temporalLabels.value[metadata[key]];
      return { key, label, value };
    });
});
const selectedCandidate = ref(null);
const pendingBulkAction = ref(null);

const candidateFilters = reactive({
  query: '',
  knowledge_base_id: null,
  origin: null,
  candidate_kind: null,
});
const proposalFilters = reactive({
  query: '',
  knowledge_base_id: null,
  status: null,
  decision: null,
});

const candidateHeaders = computed(() => [
  '',
  t('CORTEX.CURATION_V6.TABLE.CONTENT'),
  t('CORTEX.CURATION_V6.TABLE.CLASSIFICATION'),
  t('CORTEX.CURATION_V6.TABLE.SCORES'),
  t('CORTEX.CURATION_V6.TABLE.ACTIONS'),
]);
const proposalHeaders = computed(() => [
  '',
  t('CORTEX.CURATION_V6.TABLE.CONTENT'),
  t('CORTEX.CURATION_V6.TABLE.DECISION'),
  t('CORTEX.CURATION_V6.TABLE.SCORES'),
  t('CORTEX.CURATION_V6.TABLE.ACTIONS'),
]);

const option = (value, label) => ({ value, label });
const knowledgeBaseOptions = computed(() => [
  option(null, t('CORTEX.CURATION_V6.FILTERS.ALL_KNOWLEDGE_BASES')),
  ...knowledgeBases.value.map(base => option(base.id, base.name)),
]);
const originLabels = computed(() => ({
  conversation: t('CORTEX.CURATION_V6.ORIGINS.conversation'),
  playground: t('CORTEX.CURATION_V6.ORIGINS.playground'),
  document: t('CORTEX.CURATION_V6.ORIGINS.document'),
  manual: t('CORTEX.CURATION_V6.ORIGINS.manual'),
  contextual_exchange: t('CORTEX.CURATION_V6.ORIGINS.contextual_exchange'),
}));
const originOptions = computed(() => [
  option(null, t('CORTEX.CURATION_V6.FILTERS.ALL_ORIGINS')),
  ...Object.entries(originLabels.value).map(([value, label]) =>
    option(value, label)
  ),
]);
const candidateKindLabels = computed(() => ({
  reusable_qa: t('CORTEX.CURATION_V6.CANDIDATE_KINDS.reusable_qa'),
  knowledge_gap: t('CORTEX.CURATION_V6.CANDIDATE_KINDS.knowledge_gap'),
  material_conflict: t('CORTEX.CURATION_V6.CANDIDATE_KINDS.material_conflict'),
  no_change: t('CORTEX.CURATION_V6.CANDIDATE_KINDS.no_change'),
  comparison_pending: t(
    'CORTEX.CURATION_V6.CANDIDATE_KINDS.comparison_pending'
  ),
}));
const candidateKindOptions = computed(() => [
  option(null, t('CORTEX.CURATION_V6.FILTERS.ALL_CLASSIFICATIONS')),
  ...Object.entries(candidateKindLabels.value).map(([value, label]) =>
    option(value, label)
  ),
]);
const proposalStatusOptions = computed(() => [
  option(null, t('CORTEX.CURATION_V6.FILTERS.ALL_STATUSES')),
  option('pending', t('CORTEX.CURATION_V6.PROPOSAL_STATUSES.pending')),
  option('in_review', t('CORTEX.CURATION_V6.PROPOSAL_STATUSES.in_review')),
  option('failed', t('CORTEX.CURATION_V6.PROPOSAL_STATUSES.failed')),
  option('applying', t('CORTEX.CURATION_V6.PROPOSAL_STATUSES.applying')),
  option('applied', t('CORTEX.CURATION_V6.PROPOSAL_STATUSES.applied')),
  option('stale', t('CORTEX.CURATION_V6.PROPOSAL_STATUSES.stale')),
]);
const decisionLabels = computed(() => ({
  add_utterance: t('CORTEX.CURATION_V6.DECISIONS.add_utterance'),
  add_evidence: t('CORTEX.CURATION_V6.DECISIONS.add_evidence'),
  editorial_improvement: t(
    'CORTEX.CURATION_V6.DECISIONS.editorial_improvement'
  ),
  complete_answer: t('CORTEX.CURATION_V6.DECISIONS.complete_answer'),
  create_intent: t('CORTEX.CURATION_V6.DECISIONS.create_intent'),
  create_condition: t('CORTEX.CURATION_V6.DECISIONS.create_condition'),
  split: t('CORTEX.CURATION_V6.DECISIONS.split'),
  merge: t('CORTEX.CURATION_V6.DECISIONS.merge'),
  flag_conflict: t('CORTEX.CURATION_V6.DECISIONS.flag_conflict'),
  register_gap: t('CORTEX.CURATION_V6.DECISIONS.register_gap'),
}));
const decisionOptions = computed(() => [
  option(null, t('CORTEX.CURATION_V6.FILTERS.ALL_DECISIONS')),
  ...Object.entries(decisionLabels.value).map(([value, label]) =>
    option(value, label)
  ),
]);

const allCandidatesSelected = computed(
  () =>
    candidates.value.length > 0 &&
    selectedCandidateIds.value.length === candidates.value.length
);
const someCandidatesSelected = computed(
  () => selectedCandidateIds.value.length > 0 && !allCandidatesSelected.value
);
const allProposalsSelected = computed(
  () =>
    reviewableProposals.value.length > 0 &&
    selectedProposalIds.value.length === reviewableProposals.value.length
);
const someProposalsSelected = computed(
  () => selectedProposalIds.value.length > 0 && !allProposalsSelected.value
);

const score = value =>
  value === null || value === undefined || value === ''
    ? t('CORTEX.CURATION_V6.SCORE_NOT_EVALUATED')
    : Number(value).toFixed(2);
const mutationKey = (resource, id, action) =>
  `${resource}-${id}-${action}-${Date.now()}`;
const compactParams = values =>
  Object.fromEntries(
    Object.entries(values).filter(
      ([, value]) => value !== null && value !== '' && value !== undefined
    )
  );
const candidateKindLabel = value => candidateKindLabels.value[value] || value;
const decisionLabel = value => decisionLabels.value[value] || value;
const bulkConfirmLabel = computed(() =>
  pendingBulkAction.value === 'approve'
    ? t('CORTEX.CURATION_V6.BULK.APPROVE')
    : t('CORTEX.CURATION_V6.BULK.REJECT')
);

const loadKnowledgeBases = async () => {
  try {
    knowledgeBases.value = (await cortexKnowledgeBasesAPI.get()).data;
  } catch (error) {
    useAlert(t('CORTEX.CURATION_V6.API.LOAD_ERROR'));
  }
};

const loadCandidates = async () => {
  candidateLoading.value = true;
  try {
    const response = await cortexLearningCandidatesAPI.list(
      compactParams({
        status: activeAssessmentId.value ? null : 'awaiting_agent',
        assessment_id: activeAssessmentId.value,
        page: candidatePage.value,
        per_page: candidatePerPage.value,
        ...candidateFilters,
      })
    );
    candidates.value = response.data;
    candidateTotal.value = Number(
      response.headers['x-total-count'] || candidates.value.length
    );
    selectedCandidateIds.value = [];
  } catch (error) {
    useAlert(t('CORTEX.CURATION_V6.API.LOAD_ERROR'));
  } finally {
    candidateLoading.value = false;
  }
};

const loadProposals = async () => {
  proposalLoading.value = true;
  try {
    const response = await cortexCurationProposalsAPI.list(
      compactParams({
        status: proposalFilters.status || [
          'pending',
          'in_review',
          'failed',
          'applying',
        ],
        page: proposalPage.value,
        per_page: proposalPerPage.value,
        query: proposalFilters.query,
        knowledge_base_id: proposalFilters.knowledge_base_id,
        decision: proposalFilters.decision,
      })
    );
    proposals.value = response.data;
    proposalTotal.value = Number(
      response.headers['x-total-count'] || proposals.value.length
    );
    selectedProposalIds.value = [];
  } catch (error) {
    useAlert(t('CORTEX.CURATION_V6.API.LOAD_ERROR'));
  } finally {
    proposalLoading.value = false;
  }
};

const loadQueue = () =>
  Promise.all([
    loadCandidates(),
    ...(activeAssessmentId.value ? [] : [loadProposals()]),
  ]);
const clearResultScope = () => {
  activeAssessmentId.value = null;
  candidatePage.value = 1;
  return loadQueue();
};
const applyCandidateFilters = async () => {
  candidatePage.value = 1;
  await loadCandidates();
};
const applyProposalFilters = async () => {
  proposalPage.value = 1;
  await loadProposals();
};

const triage = async (candidate, actionType) => {
  actingId.value = `candidate-${candidate.id}`;
  try {
    await cortexLearningCandidatesAPI.triage(
      candidate.id,
      { action_type: actionType, lock_version: candidate.lock_version },
      mutationKey('candidate', candidate.id, actionType)
    );
    candidateDialog.value?.close();
    selectedCandidate.value = null;
    await loadQueue();
    useAlert(t('CORTEX.CURATION_V6.API.TRIAGE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] ||
        t('CORTEX.CURATION_V6.API.ACTION_ERROR')
    );
  } finally {
    actingId.value = null;
  }
};

const openCandidate = async candidate => {
  actingId.value = `candidate-${candidate.id}`;
  try {
    selectedCandidate.value = (
      await cortexLearningCandidatesAPI.show(candidate.id)
    ).data;
    candidateDialog.value?.open();
  } catch (error) {
    useAlert(t('CORTEX.CURATION_V6.API.LOAD_ERROR'));
  } finally {
    actingId.value = null;
  }
};

const bulkTriage = async actionType => {
  const selected = candidates.value.filter(candidate =>
    selectedCandidateIds.value.includes(candidate.id)
  );
  if (!selected.length) return;

  actingId.value = `candidate-bulk-${actionType}`;
  try {
    const { data } = await cortexLearningCandidatesAPI.bulkTriage(
      {
        action_type: actionType,
        items: selected.map(({ id, lock_version }) => ({ id, lock_version })),
      },
      mutationKey('candidate', 'bulk', actionType)
    );
    await loadQueue();
    useAlert(t('CORTEX.CURATION_V6.API.BULK_RESULT', data));
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] ||
        t('CORTEX.CURATION_V6.API.ACTION_ERROR')
    );
  } finally {
    actingId.value = null;
  }
};

const openReview = async proposal => {
  actingId.value = `proposal-${proposal.id}`;
  try {
    const { data } = await cortexCurationProposalsAPI.show(proposal.id);
    selectedProposal.value = data;
    editingDraft.value = false;
    resumeNeedsRefresh.value = false;
    const candidateId = data.candidate_ids?.[0];
    selectedCandidate.value = candidateId
      ? (await cortexLearningCandidatesAPI.show(candidateId)).data
      : null;
    reviewDialog.value?.open();
  } catch (error) {
    useAlert(t('CORTEX.CURATION_V6.API.LOAD_ERROR'));
  } finally {
    actingId.value = null;
  }
};

const review = async action => {
  if (
    !isReviewable(selectedProposal.value) ||
    actingId.value ||
    editingDraft.value
  )
    return;

  actingId.value = `proposal-${selectedProposal.value.id}`;
  try {
    await cortexCurationProposalsAPI[action](
      selectedProposal.value.id,
      {
        lock_version: selectedProposal.value.lock_version,
        review_digest: selectedProposal.value.review_digest,
        reason: action === 'reject' ? 'rejected_during_curation' : undefined,
      },
      mutationKey('proposal', selectedProposal.value.id, action)
    );
    reviewDialog.value?.close();
    selectedProposal.value = null;
    selectedCandidate.value = null;
    await loadQueue();
    useAlert(t('CORTEX.CURATION_V6.API.REVIEW_SUCCESS'));
  } catch {
    useAlert(t('CORTEX.CURATION_V6.API.ACTION_ERROR'));
  } finally {
    actingId.value = null;
  }
};

const prepareSelectedProposal = async () => {
  if (
    !isReviewable(selectedProposal.value) ||
    actingId.value ||
    editingDraft.value
  )
    return;
  actingId.value = 'prepare';
  try {
    await cortexCurationProposalsAPI.prepare(selectedProposal.value.id);
    useAlert(t('CORTEX.CURATION_V6.PACKAGE_QUEUED'));
  } catch (error) {
    useAlert(t('CORTEX.CURATION_V6.API.ACTION_ERROR'));
  } finally {
    actingId.value = null;
  }
};

const refreshSelectedProposal = async () => {
  if (!selectedProposal.value || editingDraft.value) return;
  try {
    const { data } = await cortexCurationProposalsAPI.show(
      selectedProposal.value.id
    );
    selectedProposal.value = data;
    resumeNeedsRefresh.value = false;
  } catch (error) {
    useAlert(t('CORTEX.CURATION_V6.API.LOAD_ERROR'));
  }
};

const editDraft = () => {
  if (!selectedProposal.value?.can_edit_draft || actingId.value) return;
  draftError.value = '';
  draftStale.value = false;
  draftOperation = null;
  editingDraft.value = true;
};
const saveDraft = async changes => {
  if (
    !editingDraft.value ||
    !selectedProposal.value?.can_edit_draft ||
    actingId.value ||
    draftStale.value
  )
    return;
  const proposal = selectedProposal.value;
  const body = {
    ...changes,
    lock_version: proposal.lock_version,
    review_digest: proposal.review_digest,
  };
  const signature = JSON.stringify(body);
  if (draftOperation?.signature !== signature)
    draftOperation = {
      signature,
      key: `draft-${crypto.getRandomValues(new Uint32Array(4)).join('-')}`,
    };
  actingId.value = 'save-draft';
  draftError.value = '';
  try {
    const { data } = await cortexCurationProposalsAPI.updateDraft(
      proposal.id,
      body,
      draftOperation.key
    );
    selectedProposal.value = data;
    editingDraft.value = false;
    await loadProposals();
    useAlert(t('CORTEX.CURATION_V6.DRAFT.SUCCESS'));
  } catch (error) {
    draftStale.value =
      error?.response?.status === 409 &&
      error?.response?.data?.error_code !== 'busy';
    draftError.value = draftStale.value
      ? t('CORTEX.CURATION_V6.DRAFT.STALE')
      : error?.response?.data?.errors?.[0] ||
        t('CORTEX.CURATION_V6.DRAFT.ERROR');
  } finally {
    actingId.value = null;
  }
};

const resumePublication = async () => {
  const proposal = selectedProposal.value;
  if (
    !proposal?.can_resume_publication ||
    resumeNeedsRefresh.value ||
    actingId.value
  )
    return;
  const body = {
    lock_version: proposal.lock_version,
    review_digest: proposal.review_digest,
  };
  const signature = JSON.stringify([proposal.id, body]);
  if (resumeOperation.value?.signature !== signature) {
    const nonce = crypto.getRandomValues(new Uint32Array(4)).join('-');
    resumeOperation.value = {
      signature,
      key: `publication-${proposal.id}-${nonce}`,
    };
  }
  actingId.value = 'resume-publication';
  try {
    const { data } = await cortexCurationProposalsAPI.resumePublication(
      proposal.id,
      body,
      resumeOperation.value.key
    );
    selectedProposal.value = data;
    resumeOperation.value = null;
    await loadProposals();
    useAlert(t('CORTEX.CURATION_V6.PUBLICATION_RESUME.QUEUED'));
  } catch (error) {
    resumeNeedsRefresh.value = error?.response?.status === 409;
    useAlert(
      error?.response?.data?.errors?.[0] ||
        t('CORTEX.CURATION_V6.API.ACTION_ERROR')
    );
  } finally {
    actingId.value = null;
  }
};

const openPublicationBudget = async () => {
  const id = selectedProposal.value?.publication_processing_run_id;
  if (!id || actingId.value) return;
  actingId.value = 'publication-budget';
  try {
    const { data } = await cortexProcessingRunsAPI.show(id);
    budgetDialog.value?.open(data);
  } catch {
    useAlert(t('CORTEX.CURATION_V6.API.LOAD_ERROR'));
  } finally {
    actingId.value = null;
  }
};

const bulkReview = async actionType => {
  const selected = proposals.value.filter(
    proposal =>
      isReviewable(proposal) && selectedProposalIds.value.includes(proposal.id)
  );
  if (!selected.length) return;

  actingId.value = `proposal-bulk-${actionType}`;
  try {
    const { data } = await cortexCurationProposalsAPI.bulkReview(
      {
        action_type: actionType,
        items: selected.map(({ id, lock_version, review_digest }) => ({
          id,
          lock_version,
          review_digest,
        })),
      },
      mutationKey('proposal', 'bulk', actionType)
    );
    bulkConfirmDialog.value?.close();
    pendingBulkAction.value = null;
    await loadQueue();
    useAlert(t('CORTEX.CURATION_V6.API.BULK_RESULT', data));
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] ||
        t('CORTEX.CURATION_V6.API.ACTION_ERROR')
    );
  } finally {
    actingId.value = null;
  }
};

const confirmBulkReview = action => {
  pendingBulkAction.value = action;
  bulkConfirmDialog.value?.open();
};
const executeBulkReview = () => bulkReview(pendingBulkAction.value);
const toggleCandidate = id => {
  selectedCandidateIds.value = selectedCandidateIds.value.includes(id)
    ? selectedCandidateIds.value.filter(selectedId => selectedId !== id)
    : [...selectedCandidateIds.value, id];
};
const toggleProposal = id => {
  selectedProposalIds.value = selectedProposalIds.value.includes(id)
    ? selectedProposalIds.value.filter(selectedId => selectedId !== id)
    : [...selectedProposalIds.value, id];
};
const toggleAllCandidates = () => {
  selectedCandidateIds.value = allCandidatesSelected.value
    ? []
    : candidates.value.map(({ id }) => id);
};
const toggleAllProposals = () => {
  selectedProposalIds.value = allProposalsSelected.value
    ? []
    : reviewableProposals.value.map(({ id }) => id);
};

onMounted(() => Promise.all([loadKnowledgeBases(), loadQueue()]));
</script>

<template>
  <div class="flex min-w-0 flex-col gap-6">
    <div
      v-if="activeAssessmentId"
      class="flex flex-wrap items-center justify-between gap-3 text-sm text-n-slate-11"
      data-testid="processing-result-scope"
    >
      <p>
        {{
          $t('CORTEX.CURATION_V6.RESULTS.DESCRIPTION', {
            id: conversationDisplayId,
          })
        }}
      </p>
      <NextButton
        type="button"
        sm
        slate
        ghost
        icon="i-lucide-list"
        :label="$t('CORTEX.CURATION_V6.RESULTS.SHOW_ALL')"
        @click="clearResultScope"
      />
    </div>
    <SectionLayout
      :title="$t('CORTEX.CURATION_V6.CANDIDATES_TITLE')"
      :description="$t('CORTEX.CURATION_V6.CANDIDATES_DESCRIPTION')"
    >
      <form
        class="mb-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4"
        @submit.prevent="applyCandidateFilters"
      >
        <NextInput
          v-model="candidateFilters.query"
          :label="$t('CORTEX.CURATION_V6.FILTERS.SEARCH')"
          :placeholder="$t('CORTEX.CURATION_V6.FILTERS.SEARCH_PLACEHOLDER')"
        />
        <WithLabel
          name="candidate-base"
          :label="$t('CORTEX.CURATION_V6.FILTERS.KNOWLEDGE_BASE')"
        >
          <AdaptiveSelect
            v-model="candidateFilters.knowledge_base_id"
            :options="knowledgeBaseOptions"
          />
        </WithLabel>
        <WithLabel
          name="candidate-origin"
          :label="$t('CORTEX.CURATION_V6.FILTERS.ORIGIN')"
        >
          <AdaptiveSelect
            v-model="candidateFilters.origin"
            :options="originOptions"
          />
        </WithLabel>
        <WithLabel
          name="candidate-kind"
          :label="$t('CORTEX.CURATION_V6.FILTERS.CLASSIFICATION')"
        >
          <AdaptiveSelect
            v-model="candidateFilters.candidate_kind"
            :options="candidateKindOptions"
          />
        </WithLabel>
        <div class="flex justify-end md:col-span-2 xl:col-span-4">
          <NextButton
            type="submit"
            sm
            icon="i-lucide-list-filter"
            :label="$t('CORTEX.CURATION_V6.FILTERS.APPLY')"
          />
        </div>
      </form>

      <div
        v-if="selectedCandidateIds.length"
        class="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-alpha-2 p-3"
      >
        <span class="text-sm text-n-slate-11">
          {{
            $t('CORTEX.CURATION_V6.BULK.SELECTED', {
              count: selectedCandidateIds.length,
            })
          }}
        </span>
        <div class="flex flex-wrap gap-2">
          <NextButton
            sm
            outline
            ruby
            icon="i-lucide-archive-x"
            :label="$t('CORTEX.CURATION_V6.BULK.DISCARD')"
            :is-loading="actingId === 'candidate-bulk-not_reusable'"
            @click="bulkTriage('not_reusable')"
          />
          <NextButton
            sm
            icon="i-lucide-send"
            :label="$t('CORTEX.CURATION_V6.BULK.SUBMIT')"
            :is-loading="actingId === 'candidate-bulk-submit_to_curation'"
            @click="bulkTriage('submit_to_curation')"
          />
        </div>
      </div>

      <BaseTable
        :headers="candidateHeaders"
        :items="candidates"
        :loading="candidateLoading"
        :no-data-message="
          activeAssessmentId
            ? $t('CORTEX.CURATION_V6.RESULTS.EMPTY')
            : $t('CORTEX.CURATION_V6.CANDIDATES_EMPTY')
        "
      >
        <template #header-0>
          <NextCheckbox
            :model-value="allCandidatesSelected"
            :indeterminate="someCandidatesSelected"
            :disabled="Boolean(activeAssessmentId)"
            :title="$t('CORTEX.CURATION_V6.SELECT_ALL')"
            @change="toggleAllCandidates"
          />
        </template>
        <template #row="{ items }">
          <BaseTableRow
            v-for="candidate in items"
            :key="candidate.id"
            :item="candidate"
          >
            <BaseTableCell class="w-5">
              <NextCheckbox
                :model-value="selectedCandidateIds.includes(candidate.id)"
                :disabled="Boolean(activeAssessmentId)"
                :title="$t('CORTEX.CURATION_V6.SELECT_ROW')"
                @change="toggleCandidate(candidate.id)"
              />
            </BaseTableCell>
            <BaseTableCell class="min-w-0">
              <div
                v-dompurify-html="
                  formatMessage(candidate.observed_question, false)
                "
                data-testid="candidate-list-question"
                class="prose prose-bubble min-w-0 max-w-none break-words font-medium text-n-slate-12 [&_p]:m-0"
              />
              <div
                v-dompurify-html="
                  formatMessage(
                    candidate.provided_answer || $t('CORTEX.CURATION_V6.GAP'),
                    false
                  )
                "
                data-testid="candidate-list-answer"
                class="prose prose-bubble mt-1 line-clamp-2 min-w-0 max-w-none break-words text-sm text-n-slate-11 [&_p]:my-1"
              />
            </BaseTableCell>
            <BaseTableCell>
              {{ candidateKindLabel(candidate.candidate_kind) }}
            </BaseTableCell>
            <BaseTableCell class="text-xs text-n-slate-11">
              {{
                $t('CORTEX.CURATION_V6.SCORE_VALUE', {
                  routing: score(candidate.routing_score),
                  publication: score(candidate.publication_readiness_score),
                  quality: score(candidate.comparative_quality_score),
                })
              }}
            </BaseTableCell>
            <BaseTableCell>
              <div class="flex justify-end gap-2">
                <NextButton
                  v-tooltip.top="$t('CORTEX.CURATION_V6.VIEW_CANDIDATE')"
                  sm
                  outline
                  slate
                  icon="i-lucide-eye"
                  class="!text-[color:inherit]"
                  :aria-label="$t('CORTEX.CURATION_V6.VIEW_CANDIDATE')"
                  :is-loading="actingId === `candidate-${candidate.id}`"
                  @click="openCandidate(candidate)"
                />
                <NextButton
                  v-if="!activeAssessmentId"
                  sm
                  outline
                  slate
                  :label="$t('CORTEX.CURATION_V6.LATER')"
                  :is-loading="actingId === `candidate-${candidate.id}`"
                  @click="triage(candidate, 'review_later')"
                />
                <NextButton
                  v-if="!activeAssessmentId"
                  sm
                  :label="$t('CORTEX.CURATION_V6.SUBMIT')"
                  :is-loading="actingId === `candidate-${candidate.id}`"
                  @click="triage(candidate, 'submit_to_curation')"
                />
              </div>
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
      <PaginationFooter
        v-model:current-page="candidatePage"
        v-model:items-per-page="candidatePerPage"
        :total-items="candidateTotal"
        show-page-size-selector
        @update:current-page="loadCandidates"
        @update:items-per-page="loadCandidates"
      />
    </SectionLayout>

    <Dialog
      ref="candidateDialog"
      width="2xl"
      overflow-y-auto
      :title="$t('CORTEX.CURATION_V6.CANDIDATE_REVIEW_TITLE')"
      :description="$t('CORTEX.CURATION_V6.CANDIDATE_REVIEW_DESCRIPTION')"
      :show-confirm-button="false"
    >
      <div v-if="selectedCandidate" class="grid gap-4 text-sm">
        <section class="grid gap-2 rounded-lg border border-n-weak p-4">
          <p class="text-xs font-medium text-n-slate-10">
            {{ $t('CORTEX.CURATION_V6.QUESTION') }}
          </p>
          <div
            v-dompurify-html="
              formatMessage(selectedCandidate.observed_question, false)
            "
            data-testid="candidate-question"
            class="prose prose-bubble min-w-0 max-w-none break-words text-n-slate-12"
          />
          <p class="text-xs font-medium text-n-slate-10">
            {{ $t('CORTEX.CURATION_V6.ANSWER') }}
          </p>
          <div
            v-dompurify-html="
              formatMessage(
                selectedCandidate.provided_answer ||
                  $t('CORTEX.CURATION_V6.GAP'),
                false
              )
            "
            data-testid="candidate-answer"
            class="prose prose-bubble min-w-0 max-w-none break-words text-n-slate-12"
          />
          <p class="text-xs font-medium text-n-slate-10">
            {{ $t('CORTEX.CURATION_V6.SOURCE') }}
          </p>
          <p class="text-n-slate-12">
            {{
              selectedCandidate.source_reference?.source_title ||
              originLabels[selectedCandidate.origin] ||
              selectedCandidate.origin
            }}
          </p>
        </section>
        <p class="text-xs text-n-slate-11">
          {{
            $t('CORTEX.CURATION_V6.SCORE_VALUE', {
              routing: score(selectedCandidate.routing_score),
              publication: score(selectedCandidate.publication_readiness_score),
              quality: score(selectedCandidate.comparative_quality_score),
            })
          }}
        </p>
        <div
          v-if="!activeAssessmentId"
          class="flex flex-wrap justify-end gap-2"
        >
          <NextButton
            outline
            ruby
            :label="$t('CORTEX.CURATION_V6.BULK.DISCARD')"
            :is-loading="actingId === `candidate-${selectedCandidate.id}`"
            @click="triage(selectedCandidate, 'not_reusable')"
          />
          <NextButton
            outline
            slate
            :label="$t('CORTEX.CURATION_V6.LATER')"
            :is-loading="actingId === `candidate-${selectedCandidate.id}`"
            @click="triage(selectedCandidate, 'review_later')"
          />
          <NextButton
            :label="$t('CORTEX.CURATION_V6.SUBMIT')"
            :is-loading="actingId === `candidate-${selectedCandidate.id}`"
            @click="triage(selectedCandidate, 'submit_to_curation')"
          />
        </div>
      </div>
    </Dialog>

    <SectionLayout
      v-if="!activeAssessmentId"
      :title="$t('CORTEX.CURATION_V6.PROPOSALS_TITLE')"
      :description="$t('CORTEX.CURATION_V6.PROPOSALS_DESCRIPTION')"
    >
      <form
        class="mb-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4"
        @submit.prevent="applyProposalFilters"
      >
        <NextInput
          v-model="proposalFilters.query"
          :label="$t('CORTEX.CURATION_V6.FILTERS.SEARCH')"
          :placeholder="$t('CORTEX.CURATION_V6.FILTERS.SEARCH_PLACEHOLDER')"
        />
        <WithLabel
          name="proposal-base"
          :label="$t('CORTEX.CURATION_V6.FILTERS.KNOWLEDGE_BASE')"
        >
          <AdaptiveSelect
            v-model="proposalFilters.knowledge_base_id"
            :options="knowledgeBaseOptions"
          />
        </WithLabel>
        <WithLabel
          name="proposal-status"
          :label="$t('CORTEX.CURATION_V6.FILTERS.STATUS')"
        >
          <AdaptiveSelect
            v-model="proposalFilters.status"
            :options="proposalStatusOptions"
          />
        </WithLabel>
        <WithLabel
          name="proposal-decision"
          :label="$t('CORTEX.CURATION_V6.FILTERS.DECISION')"
        >
          <AdaptiveSelect
            v-model="proposalFilters.decision"
            :options="decisionOptions"
          />
        </WithLabel>
        <div class="flex justify-end md:col-span-2 xl:col-span-4">
          <NextButton
            type="submit"
            sm
            icon="i-lucide-list-filter"
            :label="$t('CORTEX.CURATION_V6.FILTERS.APPLY')"
          />
        </div>
      </form>

      <div
        v-if="selectedProposalIds.length"
        class="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-alpha-2 p-3"
      >
        <span class="text-sm text-n-slate-11">
          {{
            $t('CORTEX.CURATION_V6.BULK.SELECTED', {
              count: selectedProposalIds.length,
            })
          }}
        </span>
        <div class="flex flex-wrap gap-2">
          <NextButton
            sm
            outline
            ruby
            icon="i-lucide-x"
            :label="$t('CORTEX.CURATION_V6.BULK.REJECT')"
            @click="confirmBulkReview('reject')"
          />
          <NextButton
            sm
            icon="i-lucide-check-check"
            :label="$t('CORTEX.CURATION_V6.BULK.APPROVE')"
            @click="confirmBulkReview('approve')"
          />
        </div>
      </div>

      <BaseTable
        :headers="proposalHeaders"
        :items="proposals"
        :loading="proposalLoading"
        :no-data-message="$t('CORTEX.CURATION_V6.PROPOSALS_EMPTY')"
      >
        <template #header-0>
          <NextCheckbox
            :model-value="allProposalsSelected"
            :indeterminate="someProposalsSelected"
            :title="$t('CORTEX.CURATION_V6.SELECT_ALL')"
            @change="toggleAllProposals"
          />
        </template>
        <template #row="{ items }">
          <BaseTableRow
            v-for="proposal in items"
            :key="proposal.id"
            :item="proposal"
          >
            <BaseTableCell class="w-5">
              <NextCheckbox
                :model-value="selectedProposalIds.includes(proposal.id)"
                :disabled="!isReviewable(proposal)"
                :title="$t('CORTEX.CURATION_V6.SELECT_ROW')"
                @change="toggleProposal(proposal.id)"
              />
            </BaseTableCell>
            <BaseTableCell class="min-w-0">
              <div
                v-dompurify-html="
                  formatMessage(proposal.observed_question || '', false)
                "
                data-testid="proposal-list-question"
                class="prose prose-bubble min-w-0 max-w-none break-words font-medium text-n-slate-12 [&_p]:m-0"
              />
              <div
                v-dompurify-html="
                  formatMessage(
                    proposal.provided_answer ||
                      proposal.justification ||
                      $t('CORTEX.CURATION_V6.GAP'),
                    false
                  )
                "
                data-testid="proposal-list-answer"
                class="prose prose-bubble mt-1 line-clamp-2 min-w-0 max-w-none break-words text-sm text-n-slate-11 [&_p]:my-1"
              />
            </BaseTableCell>
            <BaseTableCell>
              {{ decisionLabel(proposal.decision) }}
              <p
                v-if="
                  proposalStatusOptions.some(
                    item => item.value === proposal.status
                  )
                "
                class="mt-1 text-xs text-n-slate-11"
              >
                {{
                  proposalStatusOptions.find(
                    item => item.value === proposal.status
                  )?.label
                }}
              </p>
            </BaseTableCell>
            <BaseTableCell class="text-xs text-n-slate-11">
              {{
                $t('CORTEX.CURATION_V6.SCORE_VALUE', {
                  routing: score(proposal.routing_score),
                  publication: score(proposal.publication_readiness_score),
                  quality: score(proposal.comparative_quality_score),
                })
              }}
            </BaseTableCell>
            <BaseTableCell align="end">
              <NextButton
                sm
                outline
                slate
                :label="$t('CORTEX.CURATION_V6.REVIEW')"
                :is-loading="actingId === `proposal-${proposal.id}`"
                @click="openReview(proposal)"
              />
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
      <PaginationFooter
        v-model:current-page="proposalPage"
        v-model:items-per-page="proposalPerPage"
        :total-items="proposalTotal"
        show-page-size-selector
        @update:current-page="loadProposals"
        @update:items-per-page="loadProposals"
      />
    </SectionLayout>

    <Dialog
      ref="reviewDialog"
      width="2xl"
      overflow-y-auto
      :title="$t('CORTEX.CURATION_V6.REVIEW_TITLE')"
      :description="$t('CORTEX.CURATION_V6.REVIEW_DESCRIPTION')"
      :show-confirm-button="false"
    >
      <div v-if="selectedProposal" class="grid gap-4 text-sm">
        <section
          v-if="selectedProposal.status === 'failed'"
          class="grid gap-2"
          role="status"
        >
          <p class="text-n-slate-12">
            {{ $t('CORTEX.CURATION_V6.PUBLICATION_RESUME.DESCRIPTION') }}
          </p>
          <p
            v-if="selectedProposal.review_reason"
            class="break-words text-n-slate-11"
          >
            {{ selectedProposal.review_reason }}
          </p>
          <p v-if="resumeNeedsRefresh" class="text-n-slate-12">
            {{ $t('CORTEX.CURATION_V6.PUBLICATION_RESUME.REFRESH_REQUIRED') }}
          </p>
          <div class="flex flex-wrap justify-end gap-2">
            <NextButton
              v-if="selectedProposal.publication_processing_run_id"
              type="button"
              outline
              icon="i-lucide-wallet"
              :label="$t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.ACTION')"
              :disabled="Boolean(actingId)"
              :is-loading="actingId === 'publication-budget'"
              @click="openPublicationBudget"
            />
            <NextButton
              v-if="selectedProposal.can_resume_publication"
              type="button"
              icon="i-lucide-play"
              :label="$t('CORTEX.CURATION_V6.PUBLICATION_RESUME.ACTION')"
              :disabled="resumeNeedsRefresh || Boolean(actingId)"
              :is-loading="actingId === 'resume-publication'"
              @click="resumePublication"
            />
          </div>
        </section>
        <CortexCurationDraftEditor
          v-if="editingDraft"
          :key="selectedProposal.id"
          :proposal="selectedProposal"
          :saving="actingId === 'save-draft'"
          :stale="draftStale"
          :error="draftError"
          @save="saveDraft"
          @cancel="editingDraft = false"
        />
        <section v-else class="grid gap-2 rounded-lg border border-n-weak p-4">
          <p class="text-xs font-medium text-n-slate-10">
            {{ $t('CORTEX.CURATION_V6.QUESTION') }}
          </p>
          <p class="text-n-slate-12">
            {{
              selectedProposal.payload?.question ||
              selectedCandidate?.observed_question
            }}
          </p>
          <p
            v-if="
              observedQuestionCoverageLabel(selectedProposal.payload?.question)
            "
            class="text-xs text-n-slate-11"
          >
            {{
              observedQuestionCoverageLabel(selectedProposal.payload?.question)
            }}
          </p>
          <p
            v-if="
              observedQuestionCoverageReason(selectedProposal.payload?.question)
            "
            class="text-xs text-n-slate-11"
          >
            {{
              observedQuestionCoverageReason(selectedProposal.payload?.question)
            }}
          </p>
          <p class="text-xs font-medium text-n-slate-10">
            {{ $t('CORTEX.CURATION_V6.ANSWER') }}
          </p>
          <div
            v-dompurify-html="
              formatMessage(
                selectedProposal.payload?.answer ||
                  $t('CORTEX.CURATION_V6.GAP'),
                false
              )
            "
            class="prose prose-bubble min-w-0 max-w-none break-words text-n-slate-12"
          />
          <template v-if="selectedCandidate?.origin">
            <p class="text-xs font-medium text-n-slate-10">
              {{ $t('CORTEX.CURATION_V6.SOURCE') }}
            </p>
            <p class="text-n-slate-12">
              {{
                selectedCandidate.source_reference?.source_title ||
                originLabels[selectedCandidate.origin] ||
                selectedCandidate.origin
              }}
            </p>
          </template>
          <div v-if="selectedProposal.can_edit_draft" class="flex justify-end">
            <NextButton
              type="button"
              sm
              outline
              icon="i-lucide-pencil"
              :label="$t('CORTEX.CURATION_V6.DRAFT.EDIT')"
              :disabled="Boolean(actingId)"
              @click="editDraft"
            />
          </div>
        </section>
        <section
          v-if="selectedProposal.candidate_questions?.length > 1"
          class="grid gap-2 rounded-lg border border-n-weak p-4"
        >
          <p class="font-medium text-n-slate-12">
            {{ $t('CORTEX.CURATION_V6.OBSERVED_QUESTIONS') }}
          </p>
          <ul class="m-0 list-disc space-y-2 pl-5 text-n-slate-11">
            <li
              v-for="item in selectedProposal.candidate_questions"
              :key="item.id"
              class="break-words"
            >
              {{ item.question }}
              <span class="ml-2 text-xs">
                {{ observedQuestionCoverageLabel(item.question) }}
              </span>
              <p
                v-if="observedQuestionCoverageReason(item.question)"
                class="m-0 mt-1 text-xs"
              >
                {{ observedQuestionCoverageReason(item.question) }}
              </p>
            </li>
          </ul>
        </section>
        <section
          v-if="selectedProposal.payload?.conditions?.length"
          class="grid gap-2"
        >
          <p class="font-medium text-n-slate-12">
            {{ $t('CORTEX.CURATION_V6.CONDITIONS') }}
          </p>
          <ul class="m-0 list-disc space-y-2 pl-5 text-n-slate-11">
            <li
              v-for="condition in selectedProposal.payload.conditions"
              :key="condition"
            >
              {{ condition }}
            </li>
          </ul>
        </section>
        <section
          v-if="knowledgeFields.length"
          class="grid gap-2 rounded-lg border border-n-weak p-4"
        >
          <dl class="grid gap-3">
            <div v-for="field in knowledgeFields" :key="field.key">
              <dt class="text-xs font-medium text-n-slate-10">
                {{ field.label }}
              </dt>
              <dd
                class="m-0 mt-1 whitespace-pre-wrap break-words text-n-slate-12"
              >
                {{ field.value }}
              </dd>
            </div>
          </dl>
        </section>
        <section v-if="selectedProposal.sources?.length" class="grid gap-3">
          <p class="font-medium text-n-slate-12">
            {{ $t('CORTEX.CURATION_V6.SOURCE') }}
          </p>
          <article
            v-for="source in selectedProposal.sources"
            :key="source.source_key || source.observation_id"
            class="grid gap-2 rounded-lg border border-n-weak p-4"
          >
            <p
              v-if="source.title"
              class="m-0 break-words font-medium text-n-slate-12"
            >
              {{ source.title }}
            </p>
            <p class="m-0 whitespace-pre-wrap break-words text-n-slate-11">
              {{ source.excerpt }}
            </p>
            <p v-if="source.truncated" class="text-xs text-n-slate-10">
              {{ $t('CORTEX.CURATION_V6.EXCERPT_TRUNCATED') }}
            </p>
            <div class="flex flex-wrap gap-3">
              <a
                v-if="source.message_url"
                :href="source.message_url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-n-slate-12 underline underline-offset-2 hover:text-n-slate-11 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              >
                {{
                  $t('CORTEX.CURATION_V6.OPEN_SOURCE_MESSAGE', {
                    id: source.message_id,
                  })
                }}
              </a>
              <a
                v-if="source.document_url"
                :href="source.document_url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-n-slate-12 underline underline-offset-2 hover:text-n-slate-11 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              >
                {{
                  $t('CORTEX.CURATION_V6.OPEN_SOURCE_DOCUMENT', {
                    id: source.document_id,
                  })
                }}
              </a>
              <a
                v-for="media in source.media"
                :key="media.id"
                :href="media.url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-n-slate-12 underline underline-offset-2 hover:text-n-slate-11 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              >
                {{
                  $t('CORTEX.CURATION_V6.OPEN_SOURCE_MEDIA', { id: media.id })
                }}
              </a>
            </div>
          </article>
        </section>
        <section class="grid gap-3">
          <p
            v-if="selectedProposal.preparation_blocking_message"
            role="alert"
            class="text-n-ruby-11"
          >
            {{ selectedProposal.preparation_blocking_message }}
          </p>
          <NextButton
            v-if="selectedProposal.signals?.duplicate_proposal_id"
            type="button"
            slate
            ghost
            icon="i-lucide-arrow-right"
            :label="$t('CORTEX.CURATION_V6.OPEN_EXISTING_PACKAGE')"
            :disabled="Boolean(actingId)"
            class="justify-self-start"
            @click="
              openReview({ id: selectedProposal.signals.duplicate_proposal_id })
            "
          />
          <p
            v-if="!selectedProposal.prepared_for_review"
            class="text-n-slate-11"
          >
            {{ $t('CORTEX.CURATION_V6.PACKAGE_PENDING') }}
          </p>
          <div
            v-if="coverageReport"
            class="space-y-2"
            data-testid="question-style-coverage"
          >
            <p class="font-medium text-n-slate-12">
              {{ $t('CORTEX.CURATION_V6.STYLE_COVERAGE_TITLE') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ $t('CORTEX.CURATION_V6.STYLE_COVERAGE_DESCRIPTION') }}
            </p>
            <dl class="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
              <div
                v-for="style in styleCoverage"
                :key="style.key"
                class="flex items-center justify-between gap-2 border-b border-n-weak py-2"
              >
                <dt class="min-h-8 text-xs text-n-slate-11">
                  {{ style.label }}
                </dt>
                <dd
                  class="font-medium tabular-nums text-n-slate-12"
                  :class="{ 'text-n-amber-11': style.count === 0 }"
                >
                  {{ style.count }}
                </dd>
              </div>
            </dl>
          </div>
          <template v-for="kind in variationKinds" :key="kind.key">
            <div
              v-if="
                selectedProposal.payload?.question_variations?.[kind.key]
                  ?.length
              "
            >
              <p class="font-medium text-n-slate-12">
                {{ kind.label }}
              </p>
              <ol class="mt-2 list-decimal space-y-2 pl-5 text-n-slate-11">
                <li
                  v-for="(item, index) in selectedProposal.payload
                    .question_variations[kind.key]"
                  :key="item.question"
                  class="break-words"
                >
                  {{ item.question }}
                  <span
                    v-if="
                      kind.key === 'equivalent' && verifiedQuestionStyle(index)
                    "
                    class="ml-2 text-xs font-medium text-n-slate-11"
                  >
                    {{ verifiedQuestionStyle(index) }}
                  </span>
                  <span
                    v-if="questionCoverageLabel(kind.key, index)"
                    class="ml-2 text-xs text-n-slate-11"
                  >
                    {{ questionCoverageLabel(kind.key, index) }}
                  </span>
                  <p
                    v-if="questionCoverageReason(kind.key, index)"
                    class="m-0 mt-1 text-xs"
                  >
                    {{ questionCoverageReason(kind.key, index) }}
                  </p>
                  <p v-if="item.conditions?.length" class="m-0 mt-1 text-xs">
                    {{ $t('CORTEX.CURATION_V6.CONDITIONS') }}
                    {{ item.conditions.join(', ') }}
                  </p>
                  <p v-if="item.distinction" class="m-0 mt-1 text-xs">
                    {{ item.distinction }}
                  </p>
                </li>
              </ol>
            </div>
          </template>
          <div class="flex flex-wrap justify-end gap-2">
            <NextButton
              v-if="
                isReviewable(selectedProposal) &&
                !selectedProposal.prepared_for_review
              "
              sm
              outline
              icon="i-lucide-sparkles"
              :label="$t('CORTEX.CURATION_V6.PREPARE_PACKAGE')"
              :disabled="editingDraft || Boolean(actingId)"
              :is-loading="actingId === 'prepare'"
              @click="prepareSelectedProposal"
            />
            <NextButton
              sm
              outline
              icon="i-lucide-refresh-cw"
              :label="$t('CORTEX.CURATION_V6.REFRESH_PACKAGE')"
              :disabled="editingDraft || Boolean(actingId)"
              @click="refreshSelectedProposal"
            />
          </div>
        </section>
        <section class="grid gap-2 rounded-lg border border-n-weak p-4">
          <p class="font-medium text-n-slate-12">
            {{ $t('CORTEX.CURATION_V6.COMPARISON') }}
          </p>
          <p class="text-n-slate-11">
            {{
              $t('CORTEX.CURATION_V6.INCUMBENT', {
                id:
                  selectedProposal.comparison_snapshot
                    ?.incumbent_answer_version_id || '—',
                quality:
                  selectedProposal.comparison_snapshot
                    ?.incumbent_comparative_quality_score || '—',
              })
            }}
          </p>
          <p v-if="selectedProposal.hard_blockers?.length" class="text-ruby-11">
            {{ selectedProposal.hard_blockers.join(', ') }}
          </p>
        </section>
        <div
          v-if="isReviewable(selectedProposal)"
          class="flex justify-end gap-2"
        >
          <NextButton
            outline
            ruby
            :label="$t('CORTEX.CURATION_V6.REJECT')"
            :disabled="editingDraft || Boolean(actingId)"
            :is-loading="actingId === `proposal-${selectedProposal.id}`"
            @click="review('reject')"
          />
          <NextButton
            :label="$t('CORTEX.CURATION_V6.APPROVE')"
            :disabled="
              editingDraft ||
              Boolean(actingId) ||
              !selectedProposal.prepared_for_review ||
              selectedProposal.hard_blockers?.length > 0
            "
            :is-loading="actingId === `proposal-${selectedProposal.id}`"
            @click="review('approve')"
          />
        </div>
      </div>
    </Dialog>

    <CortexProcessingBudgetDialog
      ref="budgetDialog"
      @saved="refreshSelectedProposal"
    />

    <Dialog
      ref="bulkConfirmDialog"
      type="alert"
      :title="$t('CORTEX.CURATION_V6.BULK.CONFIRM_TITLE')"
      :description="
        $t('CORTEX.CURATION_V6.BULK.CONFIRM_DESCRIPTION', {
          count: selectedProposalIds.length,
        })
      "
      :confirm-button-label="bulkConfirmLabel"
      :is-loading="actingId === `proposal-bulk-${pendingBulkAction}`"
      @confirm="executeBulkReview"
    />
  </div>
</template>
